import { useCallback, useState } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import ContactPatientQualificationCreator, {
  type IContactPatientQualificationCreatorProps
} from "../Components/ContactPatientQualificationCreator";
import useContactPatientQualification from "../hooks/useContactPatientQualification";
import {
  IContactPatientQualificationRequest,
  IUseContactPatientQualificationProps
} from "../hooks/useContactPatientQualification";
import EnvironmentConfiguration from "../../types/EnvironmentConfiguration";
import AppContextValueProvider from "./AppContextValueProvider";

const useMockedContactPatientQualification: TriggeredHook<
  IContactPatientQualificationRequest,
  IContactPatientQualificationRequest,
  boolean,
  IUseContactPatientQualificationProps
> = () => {
  const [result, setResult] = useState<IContactPatientQualificationRequest | null>(null);

  const execute = useCallback((payload: IContactPatientQualificationRequest) => {
    // Mimic successful server echo without performing network side effects.
    setResult(payload);
  }, []);

  return {
    result,
    error: false,
    isLoading: false,
    execute
  };
};

const configuredUseContactPatientQualification = EnvironmentConfiguration.disablePatientQualificationReporting
  ? useMockedContactPatientQualification
  : useContactPatientQualification;

/** A {@link ContactPatientQualificationCreator} with qualification context injected from AppContext. */
const AppContextContactPatientQualificationCreator: React.FC = AppContextValueProvider<
  Omit<
    IContactPatientQualificationCreatorProps,
    "contactId" | "medicalForm" | "patient" | "useContactPatientQualification"
  >,
  IContactPatientQualificationCreatorProps
>({
  Component: ContactPatientQualificationCreator,
  map: ({ contactId, medicalForm, patient }) => ({
    contactId,
    medicalForm,
    patient,
    useContactPatientQualification: configuredUseContactPatientQualification
  })
}) as React.FC;

export default AppContextContactPatientQualificationCreator;
